import { h } from 'preact';
import UIElement from '../internal/UIElement/UIElement';
import collectBrowserInfo from '../../utils/browserInfo';
import AmazonPayComponent from './components/AmazonPayComponent';
import { AmazonPayElementData, AmazonPayConfiguration, CheckoutDetailsRequest, AmazonPayComponentRef } from './types';
import defaultProps from './defaultProps';
import { getCheckoutDetails } from './services';
import { TxVariants } from '../tx-variants';
import { sanitizeResponse, verifyPaymentDidNotFail } from '../internal/UIElement/utils';
import { AnalyticsInfoEvent, InfoEventType } from '../../core/Analytics/events/AnalyticsInfoEvent';

import './AmazonPay.scss';

export class AmazonPayElement extends UIElement<AmazonPayConfiguration> {
    public static readonly type = TxVariants.amazonpay;

    protected static readonly defaultProps = defaultProps;

    declare protected componentRef: AmazonPayComponentRef | undefined;

    formatProps(props: AmazonPayConfiguration): AmazonPayConfiguration {
        return {
            ...props,
            checkoutMode: props.isDropin ? 'ProcessOrder' : props.checkoutMode,
            environment: props.environment?.toUpperCase(),
            locale: props.locale?.replace('-', '_'),
            productType: props.isDropin && !props.addressDetails ? 'PayOnly' : props.productType
        };
    }

    /**
     * Formats the component data output
     */
    formatData(): AmazonPayElementData {
        const { amazonCheckoutSessionId: checkoutSessionId } = this.props;
        return {
            paymentMethod: {
                type: AmazonPayElement.type,
                ...(checkoutSessionId && { checkoutSessionId })
            },
            browserInfo: this.browserInfo
        };
    }

    protected override beforeRender(configSetByMerchant?: AmazonPayConfiguration) {
        const event = new AnalyticsInfoEvent({
            type: InfoEventType.rendered,
            component: this.type,
            configData: { ...configSetByMerchant, showPayButton: this.props.showPayButton },
            ...(configSetByMerchant?.isExpress && { isExpress: configSetByMerchant.isExpress }),
            ...(configSetByMerchant?.expressPage && { expressPage: configSetByMerchant.expressPage })
        });

        this.analytics.sendAnalytics(event);
    }

    getShopperDetails() {
        const { amazonCheckoutSessionId, configuration = {}, loadingContext, clientKey } = this.props;
        if (!amazonCheckoutSessionId) return console.error('Could not shopper details. Missing checkoutSessionId.');

        const request: CheckoutDetailsRequest = {
            checkoutSessionId: amazonCheckoutSessionId,
            getDeliveryAddress: true,
            publicKeyId: configuration?.publicKeyId || '',
            region: configuration.region
        };

        return getCheckoutDetails(loadingContext ?? '', clientKey ?? '', request);
    }

    public handleDeclineFlow() {
        const { amazonCheckoutSessionId, configuration = {}, loadingContext, clientKey } = this.props;
        if (!amazonCheckoutSessionId) return console.error('Could handle the decline flow. Missing checkoutSessionId.');

        const request: CheckoutDetailsRequest = {
            checkoutSessionId: amazonCheckoutSessionId,
            getDeclineFlowUrl: true,
            publicKeyId: configuration.publicKeyId ?? '',
            region: configuration.region
        };

        getCheckoutDetails(loadingContext ?? '', clientKey ?? '', request)
            .then((response = {}) => {
                if (!response?.declineFlowUrl) throw response;
                window.location.assign(response.declineFlowUrl);
            })
            .catch(error => {
                if (this.props.onError) this.props.onError(error, this);
            });
    }

    get isValid() {
        return true;
    }

    get browserInfo() {
        return collectBrowserInfo();
    }

    public submit(): void {
        const amazonComponentSubmitFunction = this.componentRef?.getSubmitFunction?.() || null;
        if (amazonComponentSubmitFunction) {
            return amazonComponentSubmitFunction();
        }
        this.makePaymentsCall().then(sanitizeResponse).then(verifyPaymentDidNotFail).then(this.handleResponse).catch(this.handleFailedResult);
    }

    protected override componentToRender(): h.JSX.Element {
        return (
            <AmazonPayComponent
                setComponentRef={this.setComponentRef}
                showPayButton={this.props.showPayButton}
                onClick={this.props.onClick}
                onError={this.props.onError}
                onSignOut={this.props.onSignOut}
                {...this.props}
            />
        );
    }
}

export default AmazonPayElement;
